import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { find } from 'linkifyjs';
import reactStringReplace from 'react-string-replace';
import { Modal } from 'antd';
import { parse as twParse } from 'twemoji-parser';
import { textMsgParser, xss } from '@laigu/utils';
import { parseRedbookEmoji } from '@laigu/component/business/emoji/redbook-emoji-utils';

// Vite 不支持 require()，用 import.meta.glob 预加载所有 emoji SVG
const emojiSvgModules = import.meta.glob('./components/img/emoji/*.svg', { eager: true, query: '?url', import: 'default' });
const getEmojiSvgUrl = val => emojiSvgModules[`./components/img/emoji/${val}.svg`];

import ClueMenu from '../clue-menu';

import style from './index.less';

const { emojiParser, replaceToBr } = textMsgParser;

// const CUSTOME_REGEX_NORMAL = /([0-9-_+()\s+]{5,20}|[a-zA-Z0-9-_]{5,20})/g;
/**
 * 线索抓取规则：
 * 1、超过20个字符的，取前面20个；余下的部分再尝试抓取
 * 2、以下两个正则匹配中，取结果长的那个，一样长就取原字符串中靠前的
 */
const CUSTOME_REGEX_SYMBOL = /[0-9-_+()\s+]{5,20}/g;
const CUSTOME_REGEX_WORD = /[a-zA-Z0-9-_]{5,20}/g;
function getCustomeClue(str, res) {
  const symbols = str.match(CUSTOME_REGEX_SYMBOL);
  const words = str.match(CUSTOME_REGEX_WORD);

  const results = symbols?.concat(words ?? []) ?? words;
  if (results) {
    results.sort((a, b) => str.indexOf(a) - str.indexOf(b)); // 先按子串位置排序
    results.sort((a, b) => b.length - a.length); // 再按子串长度排序
    res.push(results[0]); // 只取最优先的子串放入结果
    const rest = str.split(results[0]);
    if (rest) {
      rest.map(r => getCustomeClue(r, res)); // 剩下的部分再依次匹配
    }
  }

  return res;
}

const genATag = ({ url, text }) => {
  return `<a class="linkified" href="${url}" target="_blank" rel="noopener noreferrer" content="${text}">${text}</a>`;
};

const parseUrl = string => {
  if (!string) {
    return '';
  }
  const tempString = string.replace(/&nbsp;/g, ' ');
  // linkify无法识别&nbsp;
  const matchList = find(tempString);
  if (Array.isArray(matchList) && matchList.length > 0) {
    const totalLength = string.length;
    let latestIndex = 0;
    let result = '';
    if (matchList.length > 0) {
      matchList.forEach(val => {
        const { value: text, href: url } = val;
        const currentTxt = string.slice(latestIndex);
        const idx = latestIndex + currentTxt.indexOf(text);
        const index = idx;
        const lastIndex = index + text.length;
        const aTag = genATag({ text, url });
        if (latestIndex === index) {
          result = `${result}${aTag}`;
        } else {
          result = `${result}${string.substring(latestIndex, index)}${aTag}`;
        }
        latestIndex = lastIndex;
      });
    }
    // 拼接最后的字符串
    if (latestIndex <= totalLength - 1) {
      result = `${result}${string.substring(latestIndex, totalLength)}`;
    }
    return result;
  }
  return string;
};

const ClueCustome = ({ data, handleAddClue, handlePhoneCheck }) => {
  const matchs = getCustomeClue(data, []);
  if (matchs && matchs.length) {
    return matchs.reduce(
      (prev, s) =>
        reactStringReplace(prev, s, (match, ind) => {
          return (
            <ClueMenu
              match={match}
              handleAddClue={handleAddClue}
              key={`ClueMenu_${ind}_${match}`}
              handlePhoneCheck={handlePhoneCheck}
            />
          );
        }),
      `${data}`
    );
  }
  return data;
  // return reactStringReplace(`${data}`, CUSTOME_REGEX_NORMAL, (match, ind) => {
  //   return <ClueMenu match={match} handleAddClue={handleAddClue} key={`ClueMenu_${ind}_${match}`} />;
  // });
};

const MessageText = props => {
  const { data = '', handleAddClue = () => null, showClue = true, prewview, extra = {}, handlePhoneCheck } = props;
  const { unknown_type = 0, intercept_word = '', from_platform = '', content: otherContent = '' } = extra || {};

  // 遇到from_platform && otherContent 有值的情况代表是三方文本消息
  if (from_platform && otherContent) {
    const regex = /<img([^>]*)>/g;

    try {
      const output = otherContent.replace(regex, (match, attributes) => {
        const attributeRegex = /(\w+\s*=\s*"[^"]*")/g;
        const srcRegex = /src\s*=\s*"[^"]*"/;
        const srcAttribute = attributes.match(srcRegex)[0];
        const otherAttributes = attributes.match(attributeRegex);

        const newAttributes = [...otherAttributes, 'width="30px"'];

        return `<img ${srcAttribute} ${newAttributes.join(' ')}>`;
      });

      return <p dangerouslySetInnerHTML={{ __html: output }}></p>;
    } catch {}
  }
  // 记录 emoji 图片 url -> 原始字符的映射，用于复制时还原
  const emojiUrlMap = {};
  let html = data
    ? xss(data.replace(/&/g, '&amp;'))
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/ +/g, '&nbsp;')
        .replace(/'/g, '&#39;')
        .replace(/"/g, '&quot;')
    : data;
  try {
    // 先解析 URL(必须在 replaceToBr 之前,避免 <br /> 标签干扰 URL 识别)
    html = parseUrl(html);

    // 将换行符转换为 <br /> 标签
    html = replaceToBr(html);

    html = emojiParser(html);

    // 解析小红书表情
    html = parseRedbookEmoji(html);

    const entities = twParse(html, { buildUrl: val => getEmojiSvgUrl(val) });
    if (entities) {
      if (entities[0] && entities[0].text === html.trim() && !prewview) {
        emojiUrlMap[entities[0].url] = entities[0].text;
        html = `<span class='large_wrap'><img src=${entities[0].url} /></span>`;
      } else {
        entities.forEach(value => {
          const re = new RegExp(`${value.text}`);
          if (value.url) {
            emojiUrlMap[value.url] = value.text;
            html = html.replace(re, `<span class='base_wrap'><img src=${value.url} /></span>`);
          }
        });
      }
    }

    if (unknown_type === 3 && intercept_word) {
      const regS = new RegExp(intercept_word);
      html = html.replace(regS, `<span className=${style.intercept_word}>${intercept_word}</span>`);
    }
  } catch {}

  const handleClick = url => {
    // 历史消息服务域名保持原有打开方式
    if (!/(meiqiayun\.com|meiqia\.com|mstatik\.com|meiqia\.cn)/.test(url) || !/^http[s]?:/.test(url)) {
      Modal.confirm({
        title: '安全提示',
        content: '您要访问的内容非本站提供，可能有安全风险导致账号被盗或数据泄漏，请确定是否继续前往？',
        maskClosable: true,
        cancelText: '关闭',
        okText: '继续',
        onOk: () => {
          window.open(url);
        }
      });
    } else {
      window.open(url);
    }
  };

  const options = {
    replace: res => {
      const { attribs, children, data } = res;

      if (attribs) {
        if (attribs.class === 'large_wrap' && children) {
          return <div className={style.large_wrap}>{domToReact(children)}</div>;
        }
        if (attribs.class === 'base_wrap' && children) {
          return <span className={style.base_wrap}>{domToReact(children)}</span>;
        }
        if (attribs.class === 'linkified' && children) {
          if (attribs?.href?.includes('mailto') && showClue) {
            return (
              <ClueMenu match={attribs['content']} handleAddClue={handleAddClue} handlePhoneCheck={handlePhoneCheck} />
            );
          }

          if (prewview) {
            return <span>{domToReact(children)}</span>;
          }

          return (
            <a className={style.link} onClick={() => handleClick(attribs.href)}>
              {domToReact(children)}
            </a>
          );

        }
      }
      if (showClue && handleAddClue && data) {
        return <ClueCustome data={data} handleAddClue={handleAddClue} handlePhoneCheck={handlePhoneCheck} />;
      }
      return data;
    }
  };

  // 递归提取选中内容的纯文本，正确处理 <br> 换行和 emoji 图片
  const extractPlainText = node => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeName === 'BR') return '\n';
    if (node.nodeName === 'IMG') return emojiUrlMap[node.getAttribute('src')] || '';
    return Array.from(node.childNodes)
      .map(extractPlainText)
      .join('');
  };

  // 复制时将 emoji 图片还原为原始字符，并保留换行
  const handleCopy = e => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const fragment = selection.getRangeAt(0).cloneContents();
    e.clipboardData.setData('text/plain', extractPlainText(fragment));
    e.preventDefault();
  };

  try {
    return <span onCopy={handleCopy}>{parse(html, options)}</span>;
  } catch (e) {
    return '';
  }
};

export default MessageText;
